// src/components/coins/CoinCounter.js
//
// The 🪙 coin counter, and the coins-flying-in animation.
//
//   <CoinCounter />            live wallet total (reads wallets/{uid})
//   <CoinCounter size="lg" />  bigger, for headers
//
// Whenever coins are earned anywhere (coinService announces a
// "sbl:coins-earned" event), every counter on screen:
//   1. sends a little stream of gold coins flying from the middle of the
//      screen (or from `from`, if the event gives a point) into itself,
//   2. pops a "+N" bubble,
//   3. counts its number up to the new total.
//
// Motion uses the Web Animations API — no extra libraries — and respects
// "reduce motion" (the number just changes). Styles: coins.css (.sbl-coin*).

import CoinCelebration from "../celebrate/CoinCelebration";
import React, { useEffect, useRef, useState } from "react";
import { useWallet } from "../../services/coinService";
import "./coins.css";

/** The gold coin, drawn in CSS so it is crisp at any size. */
export const Coin = ({ size = 22, className = "" }) => (
  <span className={`sbl-coin-face ${className}`} style={{ width: size, height: size, fontSize: size * 0.55 }} aria-hidden="true">
    <span>S</span>
  </span>
);

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

/** Count from a to b over ~700ms. */
const useCountUp = (target) => {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target || prefersReducedMotion()) {
      setShown(target);
      fromRef.current = target;
      return undefined;
    }
    const start = performance.now();
    const dur = 700;
    let raf = 0;
    const step = (now) => {
      const k = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(from + (target - from) * e));
      if (k < 1) raf = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return shown;
};

/** Flies `count` coins from a point to the element, then calls done().
 *  (Not used by the counter any more — see CoinCelebration — kept for reuse.) */
// eslint-disable-next-line no-unused-vars
const flyCoins = (toEl, { count, from }, done) => {
  if (!toEl || prefersReducedMotion() || typeof document === "undefined") {
    done();
    return;
  }
  const to = toEl.getBoundingClientRect();
  if (!to.width) {
    done();
    return;
  }
  const sx = from?.x ?? window.innerWidth / 2;
  const sy = from?.y ?? window.innerHeight / 2;
  const tx = to.left + 16;
  const ty = to.top + to.height / 2;
  const n = Math.max(3, Math.min(12, count));
  let left = n;
  for (let i = 0; i < n; i += 1) {
    const el = document.createElement("span");
    el.className = "sbl-coin-fly";
    el.innerHTML = '<span class="sbl-coin-face" style="width:26px;height:26px;font-size:14px"><span>S</span></span>';
    el.style.left = `${sx - 13}px`;
    el.style.top = `${sy - 13}px`;
    document.body.appendChild(el);
    // burst outward first, then curve into the counter
    const ang = (Math.PI * 2 * i) / n + Math.random() * 0.6;
    const r = 50 + Math.random() * 50;
    const bx = Math.cos(ang) * r;
    const by = Math.sin(ang) * r - 30;
    const anim = el.animate(
      [
        { transform: "translate(0,0) scale(0.4)", opacity: 0 },
        { transform: `translate(${bx}px,${by}px) scale(1.15)`, opacity: 1, offset: 0.3 },
        { transform: `translate(${tx - sx}px,${ty - sy}px) scale(0.6)`, opacity: 0.95 },
      ],
      { duration: 900 + i * 45, easing: "cubic-bezier(.45,.05,.35,1)", delay: i * 35, fill: "forwards" }
    );
    anim.onfinish = () => {
      el.remove();
      left -= 1;
      if (left === 0) done();
    };
  }
};

const CoinCounter = ({ size = "md", className = "", label = true }) => {
  const wallet = useWallet();
  const [pending, setPending] = useState(0); // coins still in the air
  const [bubble, setBubble] = useState(null);
  const [bump, setBump] = useState(0);
  const ref = useRef(null);
  const coinsNow = useRef(wallet.coins);
  coinsNow.current = wallet.coins;
  const [floor, setFloor] = useState(0); // never show less than before the coins came

  // Hold the number back while coins are flying, so it ticks up as they land —
  // but never below what it showed before (the new total may not be here yet).
  const shown = useCountUp(pending ? Math.max(floor, wallet.coins - pending) : wallet.coins);

  useEffect(() => {
    const on = (e) => {
      const { amount, from, bank } = e.detail || {};
      // class-bank coins aren't in this (personal) counter
      if (!(amount > 0) || bank) return;
      setFloor(coinsNow.current);
      // The coins now fly into the piggy bank (CoinCelebration). The
      // counter waits for them, then ticks up with a "+N".
      setPending((p) => p + amount);
      setTimeout(() => {
        setPending((p) => Math.max(0, p - amount));
        setBubble({ amount, key: Date.now() });
        setBump((b) => b + 1);
      }, from && from.instant ? 0 : 1600);
    };
    window.addEventListener("sbl:coins-earned", on);
    return () => window.removeEventListener("sbl:coins-earned", on);
  }, []);

  return (
    <span
      ref={ref}
      className={`sbl-coins sbl-coins-${size} ${className}`}
      data-coins
      title={`${wallet.coins} coins (${wallet.lifetimeCoins} earned in all)`}
      aria-label={`${wallet.coins} coins`}
    >
      <Coin size={size === "lg" ? 30 : size === "sm" ? 18 : 24} className={bump ? "is-bump" : ""} key={bump} />
      <span className="sbl-coins-n">{shown.toLocaleString()}</span>
      {label && <span className="sbl-coins-label">coins</span>}
      {bubble && (
        <span className="sbl-coins-bubble" key={bubble.key} onAnimationEnd={() => setBubble(null)}>
          +{bubble.amount}
        </span>
      )}
      {/* the "You earned N coins!" piggy-bank popup (only one on the page listens) */}
      <CoinCelebration />
    </span>
  );
};

export default CoinCounter;
